import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  BarChart2,
  Briefcase,
  FolderKanban,
  Calendar,
  CalendarDays,
  Camera,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  DollarSign,
  FileText,
  LayoutDashboard,
  Loader2,
  Map,
  MapPin,
  Menu,
  MessageSquare,
  Package,
  PackageSearch,
  Receipt,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Table,
  User,
  UserPlus,
  Users,
  Utensils,
  Wallet,
  X,
  Zap,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom'; // Import useNavigate, Outlet, and useLocation

// IMPORTANT: These imports now refer to separate files.
// These are not directly rendered in HomePage, but are here for reference
// as they are the components that will be rendered by the <Outlet />
import ProfileDropdown from './components/Dashboard/ProfileDropdown'; // Import ProfileDropdown

// Overlay components are now imported and rendered directly in OverlayContext

// Import overlay context
import { OverlayProvider, useOverlay } from './contexts/OverlayContext';

// Import user service
import userService from './services/userService';

// Import Innoworx logo
import innoworxLogo from './assets/innoworx-logo.png';

// PWA install prompt hook
import { usePwaInstallPrompt } from './hooks/usePwaInstallPrompt';

import {
  CAMERA_ATTENDANCE_MODULE_PERMISSIONS,
  CAMERA_ATTENDANCE_SCREEN_PERMISSION,
  CAMERA_ATTENDANCE_ZONE_CONFIGS_LOCAL_PERMISSION,
} from './components/CameraAttendance/cameraAttendancePermissions';

// Simple utility to combine Tailwind classes
const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

const isPageActive = (pageId, currentPage) => {
  if (!pageId || !currentPage) return false;
  return currentPage === pageId || currentPage.startsWith(`${pageId}/`);
};

const transition = {
  type: 'spring',
  stiffness: 500,
  damping: 30,
  duration: 0.5,
};

const HomePageContent = ({ onLogout, isAuthenticated }) => {
  // Add isAuthenticated prop
  const navigate = useNavigate(); // Initialize useNavigate hook for programmatic navigation
  const location = useLocation(); // Get current location object
  const [activePage, setActivePage] = useState('dashboard'); // Keep for active styling in sidebar
  const [showInsightModal, setShowInsightModal] = useState(false);
  const [officerInsight, setOfficerInsight] = useState('');
  const [isLoadingInsight, setIsLoadingInsight] = useState(false);
  const [errorInsight, setErrorInsight] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [openNestedSubMenus, setOpenNestedSubMenus] = useState({});
  // Section panel: when a section with nested items (e.g. Kitchen & Orders) is clicked, show its screens in a slide-in panel from the left
  const [openSectionPanel, setOpenSectionPanel] = useState(null); // { moduleName, subItem }
  const [showProfileDropdown, setShowProfileDropdown] = useState(false); // State for profile dropdown
  const profileDropdownRef = useRef(null); // Ref for profile dropdown

  // User information state
  const [userInfo, setUserInfo] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [isLoadingProfileImage, setIsLoadingProfileImage] = useState(false);
  const [isFnbTenant, setIsFnbTenant] = useState(false);
  // Sidebar modules filtered by RBAC permissions
  const [filteredSidebarModules, setFilteredSidebarModules] = useState([]);
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState('');

  // PWA install prompt state
  const { canInstall, promptInstall } = usePwaInstallPrompt();

  // Get overlay data from context for display purposes
  const { datePickerData, startTimeData, endTimeData, assignedEmployees } =
    useOverlay();

  // Fetch user information on component mount
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setIsLoadingUser(true);
        const user = await userService.getCurrentUser();
        const permissions = await userService.getUserPermissions();

        setUserInfo(user);
        setUserPermissions(permissions);

        // Fetch profile image if user is available
        if (user && user.userId) {
          console.log('🖼️ User data for profile image:', {
            userId: user.userId,
            image_presigned_url: user.image_presigned_url,
            image_path: user.image_path,
            user_name: user.user_name,
          });

          setIsLoadingProfileImage(true);
          try {
            // Always use S3 path approach for consistency
            if (user.image_path) {
              console.log(
                '🖼️ Got S3 path from user data, fetching presigned URL:',
                user.image_path
              );
              // Get presigned URL from S3 path
              const presignedUrl = await userService.getS3PresignedUrl(
                user.image_path
              );
              console.log('🖼️ S3 presigned URL:', presignedUrl);
              setProfileImageUrl(presignedUrl);
            } else {
              console.log(
                '🖼️ No S3 path in user data, fetching from API for user:',
                user.userId
              );
              // Fallback to API call
              const imageUrl = await userService.getProfileImageUrl(
                user.userId
              );
              console.log('🖼️ API returned image URL:', imageUrl);
              setProfileImageUrl(imageUrl);
            }
          } catch (error) {
            console.error('Error fetching profile image:', error);
            setProfileImageUrl(null);
          } finally {
            setIsLoadingProfileImage(false);
          }
        } else {
          console.log('🖼️ No user data available for profile image');
        }
      } catch (error) {
        console.error('Error fetching user information:', error);
      } finally {
        setIsLoadingUser(false);
      }
    };

    if (isAuthenticated) {
      fetchUserInfo();
    }
  }, [isAuthenticated]);

  // Verify vertical using /tenant as well
  useEffect(() => {
    const fetchTenantVertical = async () => {
      try {
        const isFnb = await userService.isFnbTenant();
        setIsFnbTenant(Boolean(isFnb));
      } catch (e) {
        setIsFnbTenant(false);
      }
    };
    if (isAuthenticated) fetchTenantVertical();
  }, [isAuthenticated]);
  let tiDashboardLabel = 'Task and Issue Dashboard';
  if (isFnbTenant) {
    tiDashboardLabel = 'Issue Dashboard';
  } else {
    tiDashboardLabel = 'Task and Issue Dashboard';
  }

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Effect to update activePage and menu states based on URL changes
  useEffect(() => {
    const currentPathSegment = location.pathname.substring(1);
    const normalizedPath = currentPathSegment || 'dashboard';
    setActivePage(normalizedPath);

    // IMPORTANT: drive open menu/section panel off *filtered* modules (RBAC),
    // otherwise the section panel can show unauthorized screens.
    const modulesForRouting =
      Array.isArray(filteredSidebarModules) && filteredSidebarModules.length > 0
        ? filteredSidebarModules
        : sidebarModules;

    const currentModule = modulesForRouting.find(module => {
      if (module.page === normalizedPath) return true;
      if (!module.subItems) return false;
      return module.subItems.some(sub => {
        if (isPageActive(sub.page, normalizedPath)) return true;
        if (sub.subItems) {
          return sub.subItems.some(nested => isPageActive(nested.page, normalizedPath));
        }
        return false;
      });
    });

    if (currentModule && currentModule.subItems) {
      setOpenSubMenu(currentModule.name);

      const nestedSubItem = currentModule.subItems.find(
        sub =>
          sub.subItems &&
          (isPageActive(sub.page, normalizedPath) ||
            sub.subItems.some(nested => isPageActive(nested.page, normalizedPath)))
      );

      if (nestedSubItem) {
        setOpenNestedSubMenus(prev => ({
          ...prev,
          [`${currentModule.name}::${nestedSubItem.name}`]: true,
        }));
        setOpenSectionPanel({ moduleName: currentModule.name, subItem: nestedSubItem });
      } else {
        setOpenSectionPanel(null);
      }
    } else {
      setOpenSubMenu(null);
      setOpenSectionPanel(null);
    }
  }, [location.pathname, filteredSidebarModules]);

  // Effect to handle responsive sidebar collapse on tablet and mobile screens
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      
      // Tablet breakpoint: 768px - 1024px (md to lg in Tailwind)
      const isTablet = width >= 768 && width < 1024;
      // Mobile breakpoint: < 768px
      const isMobile = width < 768;
      // Desktop breakpoint: >= 1024px
      const isDesktop = width >= 1024;
      
      if (isTablet || isMobile) {
        // Collapse sidebar on tablet and mobile
        setIsSidebarOpen(false);
        setIsMobileViewport(true);
      } else if (isDesktop) {
        // Desktop: keep sidebar open by default
        setIsSidebarOpen(true);
        setIsMobileViewport(false);
      }
    };

    // Check on mount
    handleResize();

    // Listen to resize events
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const generateOfficerInsight = async officerName => {
    setIsLoadingInsight(true);
    setOfficerInsight('');
    setErrorInsight('');
    setShowInsightModal(true);
    try {
      const prompt = `Generate a brief, positive status insight for a security officer named ${officerName}, who is currently on patrol and has reported no incidents. Keep it concise, around 2-3 sentences.`;
      let chatHistory = [];
      chatHistory.push({ role: 'user', parts: [{ text: prompt }] });
      const payload = { contents: chatHistory };
      const apiKey = ''; // Replace with your actual Gemini API Key
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (
        result.candidates &&
        result.candidates.length > 0 &&
        result.candidates[0].content &&
        result.candidates[0].content.parts &&
        result.candidates[0].content.parts.length > 0
      ) {
        const text = result.candidates[0].content.parts[0].text;
        setOfficerInsight(text);
      } else {
        setErrorInsight('Failed to generate insight. Please try again.');
        console.error('Gemini API response structure unexpected:', result);
      }
    } catch (error) {
      setErrorInsight('Error communicating with Gemini API.');
      console.error('Error generating officer insight:', error);
    } finally {
      setIsLoadingInsight(false);
    }
  };

  const navigateToPage = (targetPageId, parentModuleName = null, searchQuery = null) => {
    if (!targetPageId) {
      console.warn('navigateToPage called without a target page id');
      return;
    }

    if (showInsightModal && targetPageId !== 'officer-status') {
      // Keep this for existing LLM interaction
      setShowInsightModal(false);
    }

    if (parentModuleName) {
      setOpenSubMenu(parentModuleName);
    } else {
      setOpenSubMenu(null);
    }

    const path = targetPageId === 'dashboard' ? '/dashboard' : `/${targetPageId}`;
    const search = searchQuery ? (searchQuery.startsWith('?') ? searchQuery : `?${searchQuery}`) : '';
    navigate(path + search);

    if (isMobileViewport) {
      setIsSidebarOpen(false);
    }
    console.log(
      `Navigating to ${
        parentModuleName ? `${parentModuleName} -> ` : ''
      }${targetPageId}${search ? search : ''}...`
    );
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSubMenuToggle = menuName => {
    const isOpening = openSubMenu !== menuName;
    setOpenSubMenu(isOpening ? menuName : null);

    if (!isOpening) {
      setOpenNestedSubMenus(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (key.startsWith(`${menuName}::`)) {
            delete updated[key];
          }
        });
        return updated;
      });
    }
  };

  const handleNestedSubMenuToggle = (moduleName, subItemName) => {
    const key = `${moduleName}::${subItemName}`;
    setOpenNestedSubMenus(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const openSectionPanelFor = (moduleName, subItem) => {
    if (subItem && Array.isArray(subItem.subItems) && subItem.subItems.length > 0) {
      setOpenSectionPanel({ moduleName, subItem });
      const targetPage =
        subItem.subItems.find(nested => nested.page)?.page || subItem.page;
      if (targetPage) {
        navigateToPage(targetPage, moduleName);
      }
    } else if (subItem?.page) {
      navigateToPage(subItem.page, moduleName);
    }
  };

  const closeSectionPanel = () => {
    setOpenSectionPanel(null);
  };

  // The actual logout logic is now passed as a prop from App.js
  // We simply call the passed onLogout function
  const handleProfileLogout = () => {
    console.log('Logout button clicked in ProfileDropdown.');
    if (onLogout) {
      onLogout(); // Call the logout function passed from App.js
    }
    // Clear user information
    userService.clearUserInfo();
    setUserInfo(null);
    setUserPermissions([]);
    setShowProfileDropdown(false); // Close dropdown after click
  };

  // Overlay handlers are now managed directly in OverlayContext

  // Handler for profile click, now uses useNavigate
  const handleProfileClick = () => {
    console.log('Profile clicked! Navigating to /profile.');
    navigate('/profile'); // Navigate to /profile route, matching App.js structure
    setShowProfileDropdown(false); // Close dropdown after click
  };

  const getIcon = (name, isActive = false) => {
    const colorClass = isActive ? 'text-green-700' : 'text-neutral-500';
    switch (name) {
      case 'Dashboard':
        return (
          <LayoutDashboard className={combineClasses('w-6 h-6', colorClass)} />
        );
      case 'Officer Status':
        return <Users className={combineClasses('w-6 h-6', colorClass)} />; // Using Users icon
      case 'Onboarding':
        return (
          <ClipboardList className={combineClasses('w-6 h-6', colorClass)} />
        );
      case 'Org Management':
        return <Briefcase className={combineClasses('w-6 h-6', colorClass)} />;
      case 'View Reports':
        return <FileText className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Shift Management':
        return <Calendar className={combineClasses('w-6 h-6', colorClass)} />;
      case 'schedule a patrolling':
        return <MapPin className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Leave Management':
        return (
          <CalendarDays className={combineClasses('w-6 h-6', colorClass)} />
        );
      case 'Tax':
        return <Receipt className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Chat':
        return (
          <MessageSquare className={combineClasses('w-5 h-5', colorClass)} />
        );
      case 'Profile':
        return <User className={combineClasses('w-5 h-5', colorClass)} />;
      case 'Managing':
        return <Settings className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Lost & Found':
        return <PackageSearch className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Table Booking':
        return <Table className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Cash Management':
        return <Wallet className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Service Recovery':
        return <AlertCircle className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Utilities':
        return <Zap className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Kitchen':
        return <Utensils className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Order & Service Management (FOH/BOH)':
        return (
          <ClipboardList className={combineClasses('w-6 h-6', colorClass)} />
        );
      case 'Stock & Procurement':
        return <Package className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Business Intelligence & Insights':
        return <BarChart2 className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Team & HR Management':
        return <UserPlus className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Platform Configuration & Setup':
        return <SlidersHorizontal className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Finance and Billing':
        return <DollarSign className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Finance & Billing':
        return <DollarSign className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Payments and Dues':
        return <Wallet className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Projects':
        return <FolderKanban className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Camera Attendance':
        return <Camera className={combineClasses('w-6 h-6', colorClass)} />;
      case 'Zones':
        return <Map className={combineClasses('w-6 h-6', colorClass)} />;
      default:
        return null;
    }
  };

  // Sidebar modules with permission mappings
  const sidebarModules = [
    {
      name: 'Order & Service Management (FOH/BOH)',
      icon: getIcon('Order & Service Management (FOH/BOH)'),
      permission: [
        'kitchen.view_card',
        'table_booking.view_card',
        'managing.view_card',
        'managing.lost_found.view',
        'managing.community_qr.view',
        'service_recovery.view_card',
        'flow.track_live_rides',
        'shift.create.ride',
      ],
      subItems: [
        {
          name: 'Kitchen & Orders',
          permission: 'kitchen.view_card',
          subItems: [
            { name: 'Place Order (kitchen)', page: 'place-order', permission: 'kitchen.place_order' },
            { name: 'Order Details (kitchen)', page: 'kitchen-order-details', permission: 'kitchen.order_details' },
            { name: 'Kitchen Display (KDS)', page: 'kitchen-display', permission: 'kitchen.display' },
          ],
        },
        {
          name: 'Kitchen (Dashboards)',
          permission: ['dashboard.live_order_feed'],
          subItems: [
            { name: 'Live Order Feed', page: 'dashboard-live-order-feed', permission: 'dashboard.live_order_feed' },
          ],
        },
        {
          name: 'Table Booking',
          permission: 'table_booking.view_card',
          subItems: [
            { name: 'Booking Board', page: 'table-booking', permission: 'table_booking.view' },
            { name: 'Waitlist management', page: 'table-waitlist', permission: 'waitlist.view' },
            { name: 'Floor Plan designer', page: 'table-floorplan', permission: 'floorplan.manage' },
            { name: 'Table Onboarding', page: 'table-onboarding', permission: 'table.onboarding' },
          ],
        },
        {
          name: 'Managing (Ops & Amenities)',
          permission: [
            'managing.view_card',
            'managing.lost_found.view',
            'managing.community_qr.view',
          ],
          subItems: [
            { name: 'Schedule Ops', page: 'managing-schedule', permission: 'managing.schedule_ops' },
            { name: 'Lost & Found', page: 'lost-and-found', permission: 'managing.lost_found.view' },
            {
              name: 'Community QR Management',
              page: 'qr-management',
              permission: 'managing.community_qr.view',
              subItems: [
                { name: 'All QR codes', page: 'qr-management', permission: 'managing.community_qr.view' },
                { name: 'Create QR', page: 'qr-management/create' },
              ],
            },
          ],
        },
        {
          name: 'Service Recovery',
          permission: 'service_recovery.view_card',
          subItems: [
            { name: 'Manager Alerts (service issues)', page: 'service-recovery', permission: 'service_recovery.view' },
            { name: 'Feedback Form', page: 'service-feedback', permission: 'service_recovery.analytics' },
            { name: 'Service recovery analytics', page: 'dashboard-service-recovery-analytics', permission: 'dashboard.view_card' },
          ],
        },
        {
          name: 'Rides & Patrolling',
          permission: 'flow.track_live_rides',
          subItems: [
            { name: 'Live Ride Tracking', page: 'dashboard-live-rides', permission: 'flow.track_live_rides' },
          ],
        },
      ],
    },
    {
      name: 'Payments and Dues',
      icon: getIcon('Payments and Dues'),
      subItems: [{ name: 'Dues', page: 'dues' }],
    },
    {
      name: 'Scope Sheets',
      icon: getIcon('Projects'),
      subItems: [{ name: 'View All Scopes', page: 'projects' }],
    },
    {
      name: 'Stock & Procurement',
      icon: getIcon('Stock & Procurement'),
      permission: [
        'onboarding.stock.view_in',
        'onboarding.stock.view_closing',
        'onboarding.stock.out',
        'onboarding.production.view',
        'onboarding.waste.log',
        'inventory.onboarding.view_card',
        'onboarding.product_template.view',
        'onboarding.vendor.create',
        'dashboard.view_card',
      ],
      subItems: [
        {
          name: 'Inventory & Stock Management',
          permission: ['onboarding.stock.view_in', 'onboarding.stock.view_closing', 'onboarding.stock.out', 'onboarding.production.view', 'onboarding.waste.log', 'inventory.onboarding.view_card', 'dashboard.view_card'],
          subItems: [
            { name: 'View Stock In', page: 'onboarding-stock-in', permission: 'onboarding.stock.view_in' },
            { name: 'View Closing Stock', page: 'onboarding-close-stock/view', permission: 'onboarding.stock.view_closing' },
            { name: 'Stock Out – list view', page: 'onboarding-stock-out', permission: 'onboarding.stock.out' },
            { name: 'Production', page: 'onboarding-production', permission: 'onboarding.production.view' },
            { name: 'Waste Logging', page: 'onboarding-waste-logging', permission: 'onboarding.waste.log' },
            { name: 'Inventory – Manage Inventory', page: 'inventory-manage', permission: 'inventory.onboarding.view_card' },
          ],
        },
        {
          name: 'Product & Vendor',
          permission: ['onboarding.product_template.view', 'onboarding.vendor.create'],
          subItems: [
            { name: 'Product Template management', page: 'onboarding-product-template', permission: 'onboarding.product_template.view' },
            { name: 'Vendor Onboarding', page: 'onboarding-vender-onboarding', permission: 'onboarding.vendor.create' },
          ],
        },
      ],
    },
    {
      name: 'Finance & Billing',
      icon: getIcon('Finance & Billing'),
      permission: [
        'tax.overview.view',
        'fdoc.view',
        'onboarding.bill.create',
        'onboarding.invoice.create',
        'onboarding.quotation.create',
        'interior.quotation.view',
        'interior.quotation.upload',
        'onboarding.purchase_order.view',
        'facility.booking.view',
        'dashboard.order_history',
        'cash_management.view_card',
        'pricing.view_dashboard',
        'pricing.manage_rules',
      ],
      subItems: [
        {
          name: 'Tax Overview',
          permission: ['tax.overview.view'],
          subItems: [
            { name: 'Tax Overview', page: 'tax-overview', permission: 'tax.overview.view' },
          ],
        },
        {
          name: 'Fdoc (Financial Documentation)',
          permission: [
            'fdoc.view',
            'onboarding.bill.create',
            'onboarding.invoice.create',
            'onboarding.quotation.create',
            'interior.quotation.view',
            'interior.quotation.upload',
            'onboarding.purchase_order.view',
            'facility.booking.view',
            'dashboard.order_history',
          ],
          subItems: [
            { name: 'Fdoc workspace', page: 'tax-fdoc', permission: 'fdoc.view' },
            { name: 'Bill Customer – create bill', page: 'onboarding-bill-customer', permission: 'onboarding.bill.create' },
            { name: 'Create Invoice', page: 'onboarding-create-invoice', permission: 'onboarding.invoice.create' },
            { name: 'Create Quotation', page: 'onboarding-create-quotation', permission: 'onboarding.quotation.create' },
            {
              name: 'Interior Quotation',
              page: 'onboarding-interior-quotation',
              permission: ['interior.quotation.view', 'interior.quotation.upload'],
            },
            { name: 'Purchase Orders', page: 'onboarding-purchase-order', permission: 'onboarding.purchase_order.view' },
            { name: 'Amenity Booking', page: 'managing-facility-booking', permission: 'facility.booking.view' },
            { name: 'Order History', page: 'dashboard-order-history', permission: 'dashboard.order_history' },
          ],
        },
        {
          name: 'Cash Management',
          permission: 'cash_management.view_card',
          subItems: [
            { name: 'Cash Safe Drop', page: 'cash-settlement', permission: 'cash_settlement.safe_drop' },
            { name: 'Shift Reconciliation (cash shifts)', page: 'cash-shifts', permission: 'cash_shifts.view' },
          ],
        },
        {
          name: 'Pricing',
          permission: ['pricing.view_dashboard', 'pricing.manage_rules'],
          subItems: [
            { name: 'Pricing Dashboard', page: 'dashboard-pricing-dashboard', permission: 'pricing.view_dashboard' },
            { name: 'Pricing Rules management', page: 'dashboard-pricing-rules', permission: 'pricing.manage_rules' },
          ],
        },
      ],
    },
    {
      name: 'Business Intelligence & Insights',
      icon: getIcon('Business Intelligence & Insights'),
      permission: [
        'dashboard.view_card',
        'dashboard.analytics_card',
        'dashboard.analytics.site_card',
        'dashboard.analytics.employee_overview_card',
        'dashboard.analytics.employee_attendance_card',
        'dashboard.analytics.rides_card',
        'dashboard.analytics.inventory_card',
        'dashboard.analytics.tasks_issues_card',
        'dashboard.best_performer_card',
        'reports.view.site',
        'reports.view.employee',
        'reports.view.ride',
        'reports.view.field_officer', //view employee presents report
        'tasks.view_card',
        'inventory.dashboard.view_card',
      ],
      subItems: [
        {
          name: 'Dashboard',
          permission: ['dashboard.view_card', 'dashboard.analytics_card', 'inventory.dashboard.view_card'],
          subItems: [
            {
              name: 'Live Vehicle Dashboard',
              page: 'dashboard-live-vehicle',
              permission: 'dashboard.view.live_vehicle'
            },
            { name: 'Dashboard – main overview', page: 'dashboard', permission: 'dashboard.view_card' },
            { name: 'Analytics dashboard', page: 'dashboard-analytics', permission: 'dashboard.analytics_card' },
            { name: 'Central Kitchen dashboard', page: 'dashboard-central-kitchen', permission: 'dashboard.view_card' },
            { name: 'Patrolling dashboard', page: 'dashboard-patrolling', permission: 'dashboard.view_card' },
            { name: 'Inventory Dashboard', page: 'dashboard-inventory', permission: 'inventory.dashboard.view_card' },
            { name: 'Sales Analytics', page: 'dashboard-sales-analytics', permission: 'dashboard.view_card' },
            { name: 'Stock Dashboard', page: 'dashboard-stock-dashboard', permission: 'dashboard.view_card' },
            { name: 'Wastage Dashboard', page: 'dashboard-wastage-dashboard', permission: 'dashboard.view_card' },
            { name: 'Restaurant Site Analytics', page: 'dashboard-business-analytics', permission: 'dashboard.view_card' },
            { name: 'Business Analytics', page: 'business-analytics', permission: 'dashboard.view_card' },
            { name: 'Chef Analytics', page: 'chef-analytics', permission: 'dashboard.view_card' },
            { name: 'Approval Workflow', page: 'approval-workflow', permission: 'dashboard.view_card' },
          ],
        },
        {
          name: 'Reports & Performance',
          subItems: [
            { name: 'Live Vehicle Report', page: 'reports/view/live-vehicle',permission: 'reports.view.live_vehicle' },
            { name: 'Employee Performer (best performer analytics)', page: 'dashboard-employee-performer', permission: 'dashboard.best_performer_card' },
            { name: 'View Site Report', page: 'reports/view/site', permission: 'reports.view.site' },
            { name: 'View Employee Report', page: 'reports/view/employee', permission: 'reports.view.employee' },
            { name: 'View Ride Report', page: 'reports/view/ride', permission: 'reports.view.ride' },
            { name: 'View Employee Presents Report', page: 'reports/view/field-officer', permission: 'reports.view.field_officer' },
          ],
        },
        {
          name: 'Task & Issues (T&I)',
          permission: 'tasks.view_card',
          subItems: [
            { name: tiDashboardLabel, page: 'dashboard-ti-dashboard', permission: 'tasks.view_card' },
          ],
        },
      ],
    },
    {
      name: 'Camera Attendance',
      icon: getIcon('Camera Attendance'),
      permission: CAMERA_ATTENDANCE_MODULE_PERMISSIONS,
      subItems: [
        {
          name: 'Camera Attendance Screen',
          page: 'camera-attendance',
          permission: CAMERA_ATTENDANCE_SCREEN_PERMISSION,
        },
        {
          name: 'Zone Configs (local)',
          page: 'video-attendance/zones-home',
          permission: CAMERA_ATTENDANCE_ZONE_CONFIGS_LOCAL_PERMISSION,
        },
      ],
    },
    {
      name: 'Officer Status',
      icon: getIcon('Officer Status'),
      permission: 'officer_status.view_card',
      subItems: [
        { name: 'Assigned', page: 'officer-status-assigned', permission: 'officer_status.view_card' },
        { name: 'Not Assigned', page: 'officer-status-not-assigned', permission: 'officer_status.view_card' },
      ],
    },
    {
      name: 'Team & HR Management',
      icon: getIcon('Team & HR Management'),
      permission: [
        'org_management.view_card',
        'shift_management.view_card',
        'shift.create.shift_schedule',
        'shift.create.ride',
        'labor_forecast.view',
        'shift.create.temp_leave_permission',
        'leaves.view_card',
        'leaves.apply',
        'leaves.decision',
        'leaves.apply.view',
      ],
      subItems: [
        {
          name: 'Org Management',
          permission: 'org_management.view_card',
          subItems: [
            { name: 'Org Chart', page: 'org-chart', permission: 'org_management.view_card' },
            { name: 'Delete Employee Profile', page: 'org-management-device-employee-profile', permission: ['org_management.supervisor.delete', 'org_management.guard.delete', 'org_management.spec_role.delete'] },
          ],
        },
        {
          name: 'Shift Management & Labor',
          permission: ['shift_management.view_card', 'shift.create.shift_schedule', 'shift.create.ride', 'labor_forecast.view', 'shift.create.temp_leave_permission'],
          subItems: [
            { name: 'Schedule a Shift', page: 'shift-management-schedule-a-shift', permission: 'shift.create.shift_schedule' },
            { name: 'Schedule a community shift', page: 'shift-management-schedule-a-community-shift', permission: 'shift.create.shift_community' },
            { name: 'Schedule a Ride', page: 'shift-management-schedule-a-ride', permission: 'shift.create.ride' },
            { name: 'Schedule a Patrol', page: 'schedule-a-patrolling', permission: 'shift_management.view_card' },
            { name: 'Labor Forecast', page: 'labor-forecast', permission: 'labor_forecast.view' },
            { name: 'Create temporary leave permission', page: 'shift-management-create-permission', permission: 'shift.create.temp_leave_permission' },
          ],
        },
        {
          name: 'Leave Management',
          permission: 'leaves.view_card',
          subItems: [
            { name: 'Apply Leave', page: 'leave-management/apply', permission: 'leaves.apply' },
            { name: 'Manage Requests – Applied', page: 'leave-management/manage/applied', permission: 'leaves.apply.view' },
            { name: 'Manage Requests – History', page: 'leave-management/manage/history', permission: 'leaves.decision.history' },
            { name: 'Manage Requests – Team Requests', page: 'leave-management/manage/team', permission: 'leaves.decision.pending' },
          ],
        },
      ],
    },
    {
      name: 'Platform Management',
      icon: getIcon('Platform Configuration & Setup'),
      permission: [
        'onboarding.view_card',
        'onboarding.user.create',
        'onboarding.site.create',
        'onboarding.vehicle.create',
        'onboarding.customer.create',
        'onboarding.stock.create',
        'onboarding.referral_tracker.view',
        'chat.tenant_settings.manage',
        'utilities.view_card',
        'dashboard.platform_settings',
        'auto86.view_dashboard',
        'influencers.view',
      ],
      subItems: [
        {
          name: 'Onboarding (User/Site)',
          permission: 'onboarding.view_card',
          subItems: [
            { name: 'User Onboarding', page: 'onboarding-user-onboarding', permission: 'onboarding.user.create' },
            { name: 'Bulk User Onboarding', page: 'onboarding-bulk-onboarding', permission: 'onboarding.user.create' },
            { name: 'Vehicle Onboarding', page: 'onboarding-vehicle-onboarding', permission: 'onboarding.vehicle.create' },
            { name: 'Site Onboarding', page: 'onboarding-site-onboarding', permission: 'onboarding.site.create' },
            {
              name: 'Chat Groups',
              page: 'onboarding-site-onboarding/chat',
              permission: ['chat.tenant_settings.manage', 'onboarding.site.create'],
            },
            { name: 'Customer Onboarding', page: 'onboarding-customer-onboarding', permission: 'onboarding.customer.create' },
            { name: 'Stock Onboarding', page: 'onboarding-stock-onboarding', permission: 'onboarding.stock.create' },
          ],
        },
        {
          name: 'Utilities',
          permission: 'utilities.view_card',
          subItems: [
            { name: 'Utility Matrix', page: 'utility-matrix', permission: 'utilities.view' },
            { name: 'Utility Logs', page: 'utility-logs', permission: 'utilities.manage_logs' },
            { name: 'Utility Alerts', page: 'utility-alerts', permission: 'utilities.view_alerts' },
          ],
        },
        {
          name: 'Operations & Referrals',
          permission: ['dashboard.platform_settings', 'auto86.view_dashboard', 'influencers.view'],
          subItems: [
            { name: 'Platform Settings', page: 'dashboard-platform-settings', permission: 'dashboard.platform_settings' },
            { name: 'Auto-86 / Menu availability', page: 'dashboard-auto86', permission: 'auto86.view_dashboard' },
            { name: 'Hyper-Local Influencer Referral dashboard', page: 'dashboard-influencer-referral', permission: 'influencers.view' },
            { name: 'Hyper-Local Referral Tracker', page: 'onboarding-hyper-local-referral-tracker', permission: 'onboarding.referral_tracker.view' },
          ],
        },
      ],
    },
  ];

  useEffect(() => {
    const filterModules = async () => {
      // If permissions are still loading, show empty sidebar
      if (isLoadingUser) {
        setFilteredSidebarModules([]);
        return;
      }

      // If no permissions after loading, show empty sidebar (user has no access)
      if (!userPermissions || userPermissions.length === 0) {
        console.warn('⚠️ No permissions found for user, hiding all sidebar items');
        setFilteredSidebarModules([]);
        return;
      }

      console.log('🔍 Filtering sidebar with permissions:', userPermissions);

      // Get user role for role-based restrictions
      const userRole = userInfo?.spec_role || userInfo?.role_name || userInfo?.role || '';
      const normalizedUserRole = userRole.toLowerCase();
      const isSupervisorOrAdmin = normalizedUserRole === 'supervisor' || normalizedUserRole === 'admin';

      const filtered = sidebarModules
        .map(module => {
          // Special restriction for Tax module: only supervisor and admin
          if (module.name === 'Tax' && !isSupervisorOrAdmin) {
            return null; // Hide Tax module for non-supervisor/admin users
          }

          // Check if module has permission requirement
          if (module.permission) {
            const hasModulePermission = Array.isArray(module.permission)
              ? module.permission.some(perm => userPermissions.includes(perm))
              : userPermissions.includes(module.permission);

            if (!hasModulePermission) {
              return null; // Hide entire module
            }
          }

          // Filter sub-items
          if (module.subItems) {
            const filteredSubItems = module.subItems
              .map(subItem => {
                if (subItem.permission) {
                  const hasSubPermission = Array.isArray(subItem.permission)
                    ? subItem.permission.some(perm => userPermissions.includes(perm))
                    : userPermissions.includes(subItem.permission);

                  if (!hasSubPermission) {
                    return null;
                  }
                }

                // Filter nested sub-items if they exist
                if (subItem.subItems) {
                  const filteredNested = subItem.subItems.filter(nestedItem => {
                    if (nestedItem.permission) {
                      return Array.isArray(nestedItem.permission)
                        ? nestedItem.permission.some(perm =>
                            userPermissions.includes(perm)
                          )
                        : userPermissions.includes(nestedItem.permission);
                    }
                    return true;
                  });

                  if (filteredNested.length === 0) {
                    return null;
                  }

                  return { ...subItem, subItems: filteredNested };
                }

                return subItem;
              })
              .filter(Boolean);

            // Only show module if it has at least one visible sub-item
            if (filteredSubItems.length === 0) {
              return null;
            }

            return { ...module, subItems: filteredSubItems };
          }

          return module;
        })
        .filter(Boolean);

      setFilteredSidebarModules(filtered);
    };

    filterModules();
  }, [userPermissions, isLoadingUser, userInfo, tiDashboardLabel]);

  const getPageTitle = pageId => {
    for (const module of filteredSidebarModules) {
      if (module.page === pageId) {
        return module.name;
      }
      if (module.subItems) {
        const subItem = module.subItems.find(item => item.page === pageId);
        if (subItem) {
          return `${module.name} - ${subItem.name}`;
        }
      }
    }
    // Fallback for cases where pageId might not directly map to a module/subItem name
    return pageId
      .split(/[-/]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // List of pages that have dedicated components (not placeholders)
  const pagesWithDedicatedComponents = [
    'dashboard',
    'dashboard-analytics',
    'dashboard-live-rides',
    'dashboard-live-vehicle',
    'dashboard-patrolling',
    'dashboard-employee-performer',
    'dashboard-ti-dashboard',
    'dashboard-sales-analytics',
    'dashboard-stock-dashboard',
    'dashboard-wastage-dashboard',
    'dashboard-service-recovery-analytics',
    'dashboard-influencer-referral',
    'camera-attendance',
    'camera-attendance-overstay',
    'video-attendance',
    'video-attendance/live',
    'video-attendance/zones-home',
    'video-attendance/zones',
    'profile',
    'org-chart',
    'reports/view/site',
    'reports/view/employee',
    'reports/view/ride',
    'officer-status-assigned',
    'officer-status-not-assigned',
    'reports/view/field-officer',
    'reports/view/live-vehicle',
    // Add all other routes that have dedicated components
    'onboarding-user-onboarding',
    'onboarding-bulk-onboarding',
    'onboarding-vehicle-onboarding',
    'onboarding-site-onboarding',
    'onboarding-site-onboarding/create',
    'onboarding-site-onboarding/chat',
    'onboarding-site-onboarding/chat/create',
    'onboarding-hyper-local-referral-tracker',
    'onboarding-stock-in',
    'onboarding-stock-in/create',
    'onboarding-close-stock',
    'onboarding-close-stock/view',
    'onboarding-close-stock/create',
    'onboarding-stock-out',
    'onboarding-stock-out/create',
    'onboarding-production',
    'onboarding-production/create',
    'onboarding-product-template',
    'onboarding-product-template/create',
    'onboarding-purchase-order',
    'onboarding-vender-onboarding',
    'onboarding-vender-onboarding/create',
    'onboarding-stock-onboarding',
    'onboarding-bill-customer',
    'onboarding-create-invoice',
    'onboarding-create-quotation',
    'onboarding-interior-quotation',
    'onboarding-customer-onboarding',
    'onboarding-customer-onboarding/create',
    'onboarding-waste-logging',
    'onboarding-waste-logging/create',
    'utility-matrix',
    'utility-logs',
    'utility-alerts',
    'inventory-manage',
    'tax-overview',
    'tax-fdoc',
    'org-management-device-employee-profile',
    'org-management-device-supervisor-profile',
    'shift-management-schedule-a-shift',
    'shift-management-schedule-a-community-shift',
    'shift-management-schedule-a-ride',
    'labor-forecast',
    'shift-management-schedule-a-role',
    'leave-management',
    'managing-schedule',
    'approval-workflow',
    'dashboard-business-analytics',
    'business-analytics',
    'chef-analytics',
    'managing-facility-booking',
    'managing-facility-booking-invoice',
    'managing-facility-booking-bill',
    'lost-and-found',
    'dues',
    'dues/create',
    'projects',
    'projects/create',
    'qr-management',
    'qr-management/create',
    'leave-management/apply',
    'leave-management/manage',
    'leave-management/manage/applied',
    'leave-management/manage/history',
    'leave-management/manage/team',
  ];

  // Function to get the breadcrumb path
  const getBreadcrumbPath = () => {
    const pathSegments = location.pathname.substring(1).split('/');
    const breadcrumbs = [];

    // Video Attendance (frontend): Camera Attendance > Zones Home > Zones
    if (pathSegments[0] === 'video-attendance') {
      if (pathSegments[1] === 'live') {
        breadcrumbs.push({ name: 'Camera Attendance', path: '/camera-attendance' });
        breadcrumbs.push({ name: 'Zones Home', path: '/video-attendance/zones-home' });
        breadcrumbs.push({ name: 'Live View', path: '/video-attendance/live' + location.search });
        return breadcrumbs;
      }
      if (pathSegments[1] === 'zones-home') {
        breadcrumbs.push({ name: 'Camera Attendance', path: '/camera-attendance' });
        breadcrumbs.push({ name: 'Zones Home', path: '/video-attendance/zones-home' });
        return breadcrumbs;
      }
      if (pathSegments[1] === 'zones') {
        breadcrumbs.push({ name: 'Camera Attendance', path: '/camera-attendance' });
        breadcrumbs.push({ name: 'Zones Home', path: '/video-attendance/zones-home' });
        breadcrumbs.push({ name: 'Zones', path: '/video-attendance/zones' + location.search });
        return breadcrumbs;
      }
      // Default: redirect target / compatibility
      breadcrumbs.push({ name: 'Camera Attendance', path: '/camera-attendance' });
      breadcrumbs.push({ name: 'Zones Home', path: '/video-attendance/zones-home' });
      return breadcrumbs;
    }

    // Camera Attendance logs opened from Overstay screen
    if (
      pathSegments[0] === 'camera-attendance' &&
      pathSegments[1] === 'logs' &&
      location.state?.fromOverstay === true
    ) {
      breadcrumbs.push({ name: 'Camera Attendance', path: '/camera-attendance' });
      breadcrumbs.push({
        name: 'Employee/Visitor Over Stay',
        path: '/camera-attendance-overstay',
      });
      breadcrumbs.push({ name: 'View Logs', path: location.pathname });
      return breadcrumbs;
    }

    if (pathSegments[0] === 'qr-management') {
      breadcrumbs.push({ name: 'Community QR Management', path: '/qr-management' });
      if (pathSegments[1] === 'create') {
        breadcrumbs.push({ name: 'Create QR code', path: location.pathname });
      } else if (pathSegments[1] && pathSegments[2] === 'edit') {
        breadcrumbs.push({ name: 'Edit QR', path: location.pathname });
      }
      return breadcrumbs;
    }

    if (pathSegments[0] === 'lost-and-found') {
      breadcrumbs.push({ name: 'Lost & Found', path: '/lost-and-found' });
      if (pathSegments[1] === 'create') {
        breadcrumbs.push({
          name: 'Create Lost & Found Item',
          path: location.pathname,
        });
      } else if (pathSegments[1] && pathSegments[2] === 'edit') {
        breadcrumbs.push({
          name: 'Item details',
          path: `/lost-and-found/${pathSegments[1]}`,
        });
        breadcrumbs.push({
          name: 'Edit Lost & Found Item',
          path: location.pathname,
        });
      } else if (pathSegments[1] && pathSegments[2] === 'collect') {
        breadcrumbs.push({
          name: 'Item details',
          path: `/lost-and-found/${pathSegments[1]}`,
        });
        breadcrumbs.push({
          name: 'Collect item',
          path: location.pathname,
        });
      } else if (pathSegments[1]) {
        breadcrumbs.push({
          name: 'Item details',
          path: location.pathname,
        });
      }
      return breadcrumbs;
    }

    if (pathSegments[0] === 'projects') {
      breadcrumbs.push({ name: 'Scope Sheets', path: '/projects' });
      breadcrumbs.push({ name: 'View All Scopes', path: '/projects' });
      if (pathSegments[1] === 'create') {
        breadcrumbs.push({ name: 'New Scope Sheet', path: location.pathname });
      } else if (pathSegments[1] && pathSegments[2] === 'edit') {
        breadcrumbs.push({ name: 'Edit Scope Sheet', path: location.pathname });
      } else if (pathSegments[1] && pathSegments[2] === 'preview') {
        breadcrumbs.push({ name: 'Project Preview', path: location.pathname });
      }
      return breadcrumbs;
    }

    if (pathSegments[0] === 'dues') {
      const dueFromState = location.state?.due;
      const dueNavigationState = dueFromState ? { due: dueFromState } : undefined;

      breadcrumbs.push({ name: 'Payments and Dues', path: '/dues' });
      breadcrumbs.push({ name: 'Dues', path: '/dues' });
      if (pathSegments[1] === 'create') {
        if (dueFromState) {
          const dueId = dueFromState.due_id || dueFromState.id || dueFromState.dueId;
          if (dueId) {
            breadcrumbs.push({
              name: 'View Due',
              path: `/dues/${dueId}`,
              ...(dueNavigationState ? { state: dueNavigationState } : {}),
            });
          }
          breadcrumbs.push({
            name: 'Edit Due',
            path: location.pathname,
            ...(dueNavigationState ? { state: dueNavigationState } : {}),
          });
        } else {
          breadcrumbs.push({
            name: 'Create Dues',
            path: location.pathname,
          });
        }
      } else if (pathSegments[1]) {
        breadcrumbs.push({
          name: 'View Due',
          path: location.pathname,
          ...(dueNavigationState ? { state: dueNavigationState } : {}),
        });
      }
      return breadcrumbs;
    }

    // Handle leave-management routes
    if (pathSegments[0] === 'leave-management') {
      // Add "Leave Management" as parent breadcrumb
      breadcrumbs.push({
        name: 'Leave Management',
        path: '/leave-management'
      });

      // Handle apply leave route
      if (pathSegments[1] === 'apply') {
        breadcrumbs.push({
          name: 'Apply Leave',
          path: '/leave-management/apply'
        });
        return breadcrumbs;
      }

      // Handle manage requests routes
      if (pathSegments[1] === 'manage') {
        if (pathSegments[2] === 'applied') {
          breadcrumbs.push({
            name: 'Manage Requests - Applied',
            path: '/leave-management/manage/applied'
          });
        } else if (pathSegments[2] === 'history') {
          breadcrumbs.push({
            name: 'Manage Requests - History',
            path: '/leave-management/manage/history'
          });
        } else if (pathSegments[2] === 'team') {
          breadcrumbs.push({
            name: 'Manage Requests - Team Request',
            path: '/leave-management/manage/team'
          });
        } else {
          // Default manage requests (no sub-route)
          breadcrumbs.push({
            name: 'Manage Requests',
            path: '/leave-management/manage'
          });
        }
        return breadcrumbs;
      }

      // If just /leave-management, return only the parent
      return breadcrumbs;
    }

    // Handle approval-workflow route: Dashboard > approval workflow
    if (pathSegments[0] === 'approval-workflow') {
      breadcrumbs.push({ name: 'Dashboard', path: '/dashboard' });
      breadcrumbs.push({ name: 'approval workflow', path: '/approval-workflow' });
      return breadcrumbs;
    }

    // Handle Kitchen & Orders: Live Order Feed and Order History (same hierarchy as other kitchen screens)
    if (pathSegments[0] === 'dashboard-live-order-feed') {
      breadcrumbs.push({ name: 'Kitchen & Orders', path: '/place-order' });
      breadcrumbs.push({ name: 'Live Order Feed', path: '/dashboard-live-order-feed' });
      return breadcrumbs;
    }
    if (pathSegments[0] === 'dashboard-order-history') {
      breadcrumbs.push({ name: 'Tax', path: '/tax-overview' });
      breadcrumbs.push({ name: 'Fdoc', path: '/tax-fdoc' });
      breadcrumbs.push({ name: 'Order History', path: '/dashboard-order-history' });
      return breadcrumbs;
    }

    // Handle inventory-item-detail route first (before processing segments)
    if (pathSegments[0] === 'inventory-item-detail') {
      // Add "Onboarding" as parent breadcrumb
      breadcrumbs.push({
        name: 'Onboarding',
        path: '/onboarding'
      });
      
      // Add "Manage Inventory" as parent breadcrumb
      breadcrumbs.push({
        name: 'Manage Inventory',
        path: '/inventory-manage'
      });
      
      // Check if we're on the logs page
      if (pathSegments[2] === 'logs') {
        // Add "Inventory Item Detail" breadcrumb
        breadcrumbs.push({
          name: 'Inventory Item Detail',
          path: `/${pathSegments[0]}/${pathSegments[1]}`
        });
        
        // Add "View Logs" breadcrumb
        breadcrumbs.push({
          name: 'View Logs',
          path: location.pathname
        });
      } else {
        // Add "Inventory Item Detail" breadcrumb
        breadcrumbs.push({
          name: 'Inventory Item Detail',
          path: location.pathname // Use full path including itemId
        });
      }
      
      return breadcrumbs; // Return early, don't process further segments
    }

    // Handle inventory-item-maintenance route
    if (pathSegments[0] === 'inventory-item-maintenance') {
      // Add "Onboarding" as parent breadcrumb
      breadcrumbs.push({
        name: 'Onboarding',
        path: '/onboarding'
      });
      
      // Add "Manage Inventory" as parent breadcrumb
      breadcrumbs.push({
        name: 'Manage Inventory',
        path: '/inventory-manage'
      });
      
      // Add "Inventory Item Detail" breadcrumb
      breadcrumbs.push({
        name: 'Inventory Item Detail',
        path: `/inventory-item-detail/${pathSegments[1]}`
      });
      
      // Add "Item Maintenance" breadcrumb
      breadcrumbs.push({
        name: 'Item Maintenance',
        path: location.pathname
      });
      
      return breadcrumbs; // Return early, don't process further segments
    }

    // Handle inventory-return-maintenance route
    if (pathSegments[0] === 'inventory-return-maintenance') {
      // Add "Onboarding" as parent breadcrumb
      breadcrumbs.push({
        name: 'Onboarding',
        path: '/onboarding'
      });
      
      // Add "Manage Inventory" as parent breadcrumb
      breadcrumbs.push({
        name: 'Manage Inventory',
        path: '/inventory-manage'
      });
      
      // Add "Inventory Item Detail" breadcrumb
      breadcrumbs.push({
        name: 'Inventory Item Detail',
        path: `/inventory-item-detail/${pathSegments[1]}`
      });
      
      // Add "Return Maintenance" breadcrumb
      breadcrumbs.push({
        name: 'Return Maintenance',
        path: location.pathname
      });
      
      return breadcrumbs; // Return early, don't process further segments
    }

    // Handle onboarding-inventory-item-onboarding route
    if (pathSegments[0] === 'onboarding-inventory-item-onboarding') {
  // Add "Onboarding" as parent breadcrumb
  breadcrumbs.push({
    name: 'Onboarding',
    path: '/onboarding'
  });

  // Add "Manage Inventory" as parent breadcrumb
  breadcrumbs.push({
    name: 'Manage Inventory',
    path: '/inventory-manage'
  });

  // Add "Inventory Item Onboarding" breadcrumb
  breadcrumbs.push({
    name: 'Inventory Item Onboarding',
    path: location.pathname
  });

  return breadcrumbs; // Return early, don't process further segments
}

/* ---------- MERGED BLOCK START ---------- */

// STOCK-IN CREATE FLOW  (from dashboard-updates branch)
if (pathSegments[0] === 'onboarding-stock-in' && pathSegments[1] === 'create') {

  breadcrumbs.push({
    name: 'Onboarding',
    path: '/onboarding'
  });
  breadcrumbs.push({
    name: 'Stock In',
    path: '/onboarding-stock-in'
  });
  breadcrumbs.push({
    name: 'Stock In Details',
    path: '/onboarding-stock-in/create'
  });

  return breadcrumbs;
}

// STOCK-OUT FLOW
// onboarding-stock-out route (list view) – Section: Inventory & Stock Management
if (pathSegments[0] === 'onboarding-stock-out' && !pathSegments[1]) {
  breadcrumbs.push({
    name: 'Inventory & Stock Management',
    path: '/onboarding-stock-in'
  });
  breadcrumbs.push({
    name: 'Stock Out – list view',
    path: '/onboarding-stock-out'
  });

  return breadcrumbs;
}

// onboarding-stock-out/create route
if (pathSegments[0] === 'onboarding-stock-out' && pathSegments[1] === 'create') {
  const isEditMode = typeof window !== 'undefined' &&
    (location.state?.editMode === true ||
     location.state?.entry !== undefined && location.state?.entry !== null ||
     location.state?.items !== undefined && location.state?.items !== null);

  breadcrumbs.push({
    name: 'Inventory & Stock Management',
    path: '/onboarding-stock-in'
  });
  breadcrumbs.push({
    name: 'Stock Out – list view',
    path: '/onboarding-stock-out'
  });

  if (isEditMode) {
    breadcrumbs.push({
      name: 'Stock out details',
      path: '/onboarding-stock-out/create'
    });
  } else {
    breadcrumbs.push({
      name: 'Create Stock Out',
      path: '/onboarding-stock-out/create'
    });
  }

  return breadcrumbs;
}

/* ---------- ClosING STOCK FLOW (from main branch) ---------- */

// onboarding-close-stock/view route – Section: Inventory & Stock Management
if (pathSegments[0] === 'onboarding-close-stock' && pathSegments[1] === 'view') {
  breadcrumbs.push({
    name: 'Inventory & Stock Management',
    path: '/onboarding-stock-in'
  });
  breadcrumbs.push({
    name: 'View Closing Stock',
    path: location.pathname
  });

  return breadcrumbs;
}

// onboarding-close-stock/create route
if (pathSegments[0] === 'onboarding-close-stock' && pathSegments[1] === 'create') {
  const isViewMode = typeof window !== 'undefined' &&
    (location.state?.viewMode === true ||
     location.state?.editMode === true ||
     location.state?.items !== undefined && location.state?.items !== null ||
     sessionStorage.getItem('closingStockViewMode') === 'true');

  breadcrumbs.push({
    name: 'Inventory & Stock Management',
    path: '/onboarding-stock-in'
  });
  breadcrumbs.push({
    name: 'View Closing Stock',
    path: '/onboarding-close-stock/view'
  });

  if (isViewMode) {
    breadcrumbs.push({
      name: 'View Close Stock',
      path: '/onboarding-close-stock/create'
    });
  } else {
    breadcrumbs.push({
      name: 'Create Closing Stock',
      path: '/onboarding-close-stock/create'
    });
  }

  return breadcrumbs;
}

// main closing stock page
if (pathSegments[0] === 'onboarding-close-stock' && !pathSegments[1]) {
  const navigatedFromView = typeof window !== 'undefined' &&
    sessionStorage.getItem('navigatedFromCloseStockView') === 'true';

  breadcrumbs.push({
    name: 'Inventory & Stock Management',
    path: '/onboarding-stock-in'
  });

  if (navigatedFromView) {
    breadcrumbs.push({
      name: 'View Closing Stock',
      path: '/onboarding-close-stock/view'
    });
  }

  breadcrumbs.push({
    name: 'Closing Stock',
    path: location.pathname
  });

  return breadcrumbs;
}

/* ---------- PURCHASE ORDER FLOW ---------- */

// onboarding-purchase-order/view route
if (pathSegments[0] === 'onboarding-purchase-order' && pathSegments[1] === 'view') {
  // Check if navigated from Fdoc (via location.state or referrer)
  const navigatedFromFdoc = typeof window !== 'undefined' && 
    (location.state?.fromFdoc === true || 
     document.referrer.includes('/tax-fdoc'));
  
  if (navigatedFromFdoc) {
    // Show Tax > Fdoc > View Purchase Order breadcrumbs when from Fdoc
    breadcrumbs.push({
      name: 'Tax',
      path: '/tax-overview'
    });
    breadcrumbs.push({
      name: 'Fdoc',
      path: '/tax-fdoc'
    });
    breadcrumbs.push({
      name: 'View Purchase Order',
      path: location.pathname
    });
  } else {
    breadcrumbs.push({
      name: 'Tax',
      path: '/tax-overview'
    });
    breadcrumbs.push({
      name: 'Fdoc',
      path: '/tax-fdoc'
    });
    breadcrumbs.push({
      name: 'View Purchase Orders',
      path: location.pathname
    });
  }

  return breadcrumbs;
}

// main purchase order page
if (pathSegments[0] === 'onboarding-purchase-order' && !pathSegments[1]) {
  const navigatedFromView = typeof window !== 'undefined' &&
    sessionStorage.getItem('navigatedFromPurchaseOrderView') === 'true';

  breadcrumbs.push({
    name: 'Tax',
    path: '/tax-overview'
  });
  breadcrumbs.push({
    name: 'Fdoc',
    path: '/tax-fdoc'
  });

  if (navigatedFromView) {
    breadcrumbs.push({
      name: 'View Purchase Orders',
      path: '/onboarding-purchase-order/view'
    });
  }

  breadcrumbs.push({
    name: 'Purchase Order',
    path: location.pathname
  });

  return breadcrumbs;
}

/* ---------- INVOICE ROUTES ---------- */
// Handle onboarding-create-invoice (Create Invoice page) when opened from Fdoc (clicked invoice card)
if (pathSegments[0] === 'onboarding-create-invoice' && !pathSegments[1]) {
  const fromFdoc = location.state?.fromFdoc === true || !!location.state?.invoice;
  if (fromFdoc) {
    breadcrumbs.push({
      name: 'Tax',
      path: '/tax-overview'
    });
    breadcrumbs.push({
      name: 'Fdoc',
      path: '/tax-fdoc',
      state: {
        restoreFdoc: true,
        selectedFdocType: location.state?.fdocType,
        selectedSite: location.state?.fdocSite,
        selectedMonth: location.state?.fdocMonth
      }
    });
    breadcrumbs.push({
      name: 'Create Invoice',
      path: location.pathname
    });
    return breadcrumbs;
  }
}

// Handle onboarding-create-invoice/view route
if (pathSegments[0] === 'onboarding-create-invoice' && pathSegments[1] === 'view') {
  breadcrumbs.push({
    name: 'Tax',
    path: '/tax-overview'
  });
  breadcrumbs.push({
    name: 'Fdoc',
    path: '/tax-fdoc'
  });
  breadcrumbs.push({
    name: 'View Invoice',
    path: location.pathname
  });

  return breadcrumbs;
}

/* ---------- BILL VIEW ROUTE ---------- */
// Handle onboarding-bill-customer/view route
if (pathSegments[0] === 'onboarding-bill-customer' && pathSegments[1] === 'view') {
  breadcrumbs.push({
    name: 'Tax',
    path: '/tax-overview'
  });
  breadcrumbs.push({
    name: 'Fdoc',
    path: '/tax-fdoc'
  });
  breadcrumbs.push({
    name: 'View Bill',
    path: location.pathname
  });
  
  return breadcrumbs;
}

/* ---------- QUOTATION VIEW ROUTE ---------- */
// Handle onboarding-create-quotation/view route
if (pathSegments[0] === 'onboarding-create-quotation' && pathSegments[1] === 'view') {
  // Check if navigated from Fdoc (via location.state or referrer)
  const navigatedFromFdoc = typeof window !== 'undefined' && 
    (location.state?.fromFdoc === true || 
     document.referrer.includes('/tax-fdoc'));
  
  if (navigatedFromFdoc) {
    // Show Tax > Fdoc > View Quotation breadcrumbs when from Fdoc
    breadcrumbs.push({
      name: 'Tax',
      path: '/tax-overview'
    });
    breadcrumbs.push({
      name: 'Fdoc',
      path: '/tax-fdoc'
    });
    breadcrumbs.push({
      name: 'View Quotation',
      path: location.pathname
    });
  } else {
    breadcrumbs.push({
      name: 'Tax',
      path: '/tax-overview'
    });
    breadcrumbs.push({
      name: 'Fdoc',
      path: '/tax-fdoc'
    });
    breadcrumbs.push({
      name: 'View Quotation',
      path: location.pathname
    });
  }
  
  return breadcrumbs;
}

    /* ---------- MERGE COMPLETE ---------- */

    if (pathSegments[0] === 'managing-facility-booking' && !pathSegments[1]) {
      breadcrumbs.push({ name: 'Tax', path: '/tax-overview' });
      breadcrumbs.push({ name: 'Fdoc', path: '/tax-fdoc' });
      breadcrumbs.push({ name: 'Amenity Booking', path: '/managing-facility-booking' });
      return breadcrumbs;
    }

    // Handle managing-facility-booking-invoice route
    if (pathSegments[0] === 'managing-facility-booking-invoice') {
      breadcrumbs.push({ name: 'Tax', path: '/tax-overview' });
      breadcrumbs.push({ name: 'Fdoc', path: '/tax-fdoc' });
      breadcrumbs.push({
        name: 'Amenity Booking',
        path: '/managing-facility-booking'
      });
      breadcrumbs.push({
        name: 'Amenity Booking Invoice',
        path: location.pathname
      });
      return breadcrumbs;
    }

    // Handle managing-facility-booking-bill route
    if (pathSegments[0] === 'managing-facility-booking-bill') {
      breadcrumbs.push({ name: 'Tax', path: '/tax-overview' });
      breadcrumbs.push({ name: 'Fdoc', path: '/tax-fdoc' });
      breadcrumbs.push({
        name: 'Amenity Booking',
        path: '/managing-facility-booking'
      });
      breadcrumbs.push({
        name: 'Amenity Booking Bill',
        path: location.pathname
      });
      return breadcrumbs;
    }

    // Handle dashboard-all-stock-items route
    if (pathSegments[0] === 'dashboard-all-stock-items') {
      breadcrumbs.push({
        name: 'Dashboard',
        path: '/dashboard'
      });
      breadcrumbs.push({
        name: 'Stock Dashboard',
        path: '/dashboard-stock-dashboard'
      });
      breadcrumbs.push({
        name: 'All Stock Items',
        path: location.pathname
      });
      return breadcrumbs; // Return early, don't process further segments
    }

    // Handle report routes – Section: Reporting & Performance
    if (pathSegments[0] === 'reports' && pathSegments[1] === 'view') {
      breadcrumbs.push({
        name: 'Reporting & Performance',
        path: '/dashboard-employee-performer'
      });

      if (pathSegments[2] === 'site') {
        breadcrumbs.push({
          name: 'View Site Report',
          path: location.pathname
        });
      } else if (pathSegments[2] === 'employee') {
        breadcrumbs.push({
          name: 'View Employee Report',
          path: location.pathname
        });
      } else if (pathSegments[2] === 'ride') {
        breadcrumbs.push({
          name: 'View Ride Report',
          path: location.pathname
        });
      } else if (pathSegments[2] === 'field-officer') {
        breadcrumbs.push({
          name: 'View Employee Presents Report',
          path: location.pathname
        });
      } else if (pathSegments[2] === 'live-vehicle') {
        breadcrumbs.push({
          name: 'Live Vehicle Report',
          path: location.pathname
        });
      }

      return breadcrumbs;
    }

    // Site onboarding: community setup (Property Structure / Amenities)
    if (
      pathSegments[0] === 'onboarding-site-onboarding' &&
      pathSegments[2] === 'community'
    ) {
      breadcrumbs.push({
        name: 'Onboarding',
        path: '/onboarding',
      });
      breadcrumbs.push({
        name: 'Site Onboarding',
        path: '/onboarding-site-onboarding',
      });
      const siteName = location.state?.siteName || 'Site';
      breadcrumbs.push({
        name: siteName,
        path: `/onboarding-site-onboarding/${pathSegments[1]}/community`,
        state: location.state,
      });
      const stepParam = new URLSearchParams(location.search).get('step');
      breadcrumbs.push({
        name: stepParam === 'amenities' ? 'Amenities' : 'Property Structure',
        path: location.pathname + location.search,
      });
      return breadcrumbs;
    }

    // Site onboarding: create chat group
    if (
      pathSegments[0] === 'onboarding-site-onboarding' &&
      pathSegments[1] === 'chat' &&
      pathSegments[2] === 'create'
    ) {
      breadcrumbs.push({
        name: 'Onboarding',
        path: '/onboarding',
      });
      breadcrumbs.push({
        name: 'Site Onboarding',
        path: '/onboarding-site-onboarding',
      });
      breadcrumbs.push({
        name: 'Chat Groups',
        path: '/onboarding-site-onboarding/chat',
      });
      breadcrumbs.push({
        name: 'Create Group',
        path: location.pathname,
      });
      return breadcrumbs;
    }

    // Site onboarding: chat group management
    if (
      pathSegments[0] === 'onboarding-site-onboarding' &&
      pathSegments[1] === 'chat'
    ) {
      breadcrumbs.push({
        name: 'Onboarding',
        path: '/onboarding',
      });
      breadcrumbs.push({
        name: 'Site Onboarding',
        path: '/onboarding-site-onboarding',
      });
      breadcrumbs.push({
        name: 'Chat Groups',
        path: location.pathname,
      });
      return breadcrumbs;
    }

    // Site onboarding: create site
    if (
      pathSegments[0] === 'onboarding-site-onboarding' &&
      pathSegments[1] === 'create'
    ) {
      breadcrumbs.push({
        name: 'Onboarding',
        path: '/onboarding',
      });
      breadcrumbs.push({
        name: 'Site Onboarding',
        path: '/onboarding-site-onboarding',
      });
      breadcrumbs.push({
        name: 'Create Site',
        path: location.pathname,
      });
      return breadcrumbs;
    }

    // Process each path segment
    pathSegments.forEach((segment, index) => {
      if (!segment) return; // Skip empty segments
      
      let found = false;

      // Check top-level modules first (but skip dashboard as it should be treated as sub-item)
      for (const module of filteredSidebarModules) {
        if (module.page === segment && module.page !== 'dashboard') {
          breadcrumbs.push({
            name: module.name,
            path: `/${module.page}`,
          });
          found = true;
          break;
        }
      }

      // If not found in top-level modules, check sub-items (including nested)
      if (!found) {
        // First check for 3-level: Module > Section (subItem) > Screen (nestedItem)
        for (const module of filteredSidebarModules) {
          if (!module.subItems) continue;
          for (const subItem of module.subItems) {
            if (!subItem.subItems) continue;
            const nestedItem = subItem.subItems.find(nested => nested.page === segment);
            if (nestedItem) {
              // Section > Screen (e.g. "Kitchen & Orders" > "Place Order (kitchen)")
              const sectionPath = subItem.subItems[0]?.page ? `/${subItem.subItems[0].page}` : '#';
              breadcrumbs.push({
                name: subItem.name,
                path: sectionPath,
              });
              breadcrumbs.push({
                name: nestedItem.name,
                path: `/${nestedItem.page}`,
              });
              found = true;
              break;
            }
          }
          if (found) break;
        }

        // Then check for 2-level: Module (section) > Screen (subItem)
        if (!found) {
          for (const module of filteredSidebarModules) {
            if (module.subItems) {
              const subItem = module.subItems.find(sub => sub.page === segment);
              if (subItem) {
                // Section > Screen (e.g. "Officer Status" > "Assigned")
                let parentPath;
                if (module.name === 'Dashboard') {
                  parentPath = '/dashboard';
                } else if (module.page) {
                  parentPath = `/${module.page}`;
                } else {
                  const moduleNameLower = module.name.toLowerCase().replace(/\s+/g, '-');
                  parentPath = `/${moduleNameLower}`;
                }

                const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
                const shouldAddParent = !lastBreadcrumb ||
                                       lastBreadcrumb.path !== parentPath ||
                                       (lastBreadcrumb.name === 'Dashboard' && module.name !== 'Dashboard');

                if (shouldAddParent) {
                  breadcrumbs.push({
                    name: module.name,
                    path: parentPath,
                  });
                }

                breadcrumbs.push({
                  name: subItem.name,
                  path: `/${subItem.page}`,
                });
                found = true;
                break;
              }
            }
          }
        }
      }

      // Handle zoomed chart routes
      if (!found) {
        const zoomedChartTitles = {
          'dashboard-ti-dashboard-zoomed-pending-issues': 'Pending Issues',
          'dashboard-ti-dashboard-zoomed-track-issue-completion': 'Track and Issue Completion',
          'dashboard-ti-dashboard-zoomed-delay-analysis': 'Delay Analysis',
          'dashboard-ti-dashboard-zoomed-issues-raised-solved': 'Issues Raised and Solved'
        };

        if (zoomedChartTitles[segment]) {
          // Only add Dashboard parent if not already present
          const hasDashboard = breadcrumbs.some(crumb => crumb.path === '/dashboard');
          if (!hasDashboard) {
            breadcrumbs.push({
              name: 'Dashboard',
              path: '/dashboard'
            });
          }
          
          // Add Issue Dashboard
          breadcrumbs.push({
            name: tiDashboardLabel,
            path: '/dashboard-ti-dashboard'
          });
          
          // Add the chart name
          breadcrumbs.push({
            name: zoomedChartTitles[segment],
            path: `/${segment}`
          });
          found = true;
        }
      }

      // Fallback for unknown routes
      if (!found && index === pathSegments.length - 1) {
        const title = segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        breadcrumbs.push({ 
          name: title, 
          path: `/${segment}` 
        });
      }
    });
    
    // Check for query parameters to add additional breadcrumbs
    // Specifically handle "Ride Details" and "Create Ride Schedule" views for Schedule a Ride
    const searchParams = new URLSearchParams(location.search);
    const viewParam = searchParams.get('view');
    const rideIdParam = searchParams.get('rideId');
    const isEditMode = !!rideIdParam;
    
    if (location.pathname === '/shift-management-schedule-a-ride') {
      if (viewParam === 'ride-details') {
        // Add "Ride Details" breadcrumb if we're viewing ride details
        breadcrumbs.push({
          name: 'Ride Details',
          path: location.pathname + location.search, // Keep current path with query params
        });
      } else if (viewParam === 'create-ride') {
        // If in edit mode, add "Ride Details" breadcrumb first (since edit comes from ride details)
        if (isEditMode) {
          const rideDetailsParams = new URLSearchParams();
          rideDetailsParams.set('view', 'ride-details');
          rideDetailsParams.set('rideId', rideIdParam);
          breadcrumbs.push({
            name: 'Ride Details',
            path: location.pathname + '?' + rideDetailsParams.toString(),
          });
        }
        // Add "Create Ride Schedule" or "Edit Ride Schedule" breadcrumb based on edit mode
        breadcrumbs.push({
          name: isEditMode ? 'Edit Ride Schedule' : 'Create Ride Schedule',
          path: location.pathname + location.search, // Keep current path with query params
        });
      } else if (viewParam === 'assign-convoy') {
        // Add breadcrumbs: if in edit mode, include "Ride Details" > "Edit Ride Schedule" > "Edit Convoy"
        // Otherwise: "Create Ride Schedule" > "Convoy Assignment"
        if (isEditMode) {
          // Add "Ride Details" breadcrumb first
          const rideDetailsParams = new URLSearchParams();
          rideDetailsParams.set('view', 'ride-details');
          rideDetailsParams.set('rideId', rideIdParam);
          breadcrumbs.push({
            name: 'Ride Details',
            path: location.pathname + '?' + rideDetailsParams.toString(),
          });
        }
        // Add "Create Ride Schedule"/"Edit Ride Schedule" breadcrumb
        const createRideParams = new URLSearchParams();
        createRideParams.set('view', 'create-ride');
        if (rideIdParam) {
          createRideParams.set('rideId', rideIdParam);
        }
        breadcrumbs.push({
          name: isEditMode ? 'Edit Ride Schedule' : 'Create Ride Schedule',
          path: location.pathname + '?' + createRideParams.toString(),
        });
        breadcrumbs.push({
          name: isEditMode ? 'Edit Convoy' : 'Convoy Assignment',
          path: location.pathname + location.search, // Keep current path with query params
        });
      }
    }
    
    // Handle breadcrumbs for Schedule a Patrolling
    if (location.pathname === '/schedule-a-patrolling') {
      const patrolIdParam = searchParams.get('patrolId');
      const isPatrolEditMode = !!patrolIdParam;
      
      if (viewParam === 'patrol-details') {
        // Add "Patrol Details" breadcrumb if we're viewing patrol details
        breadcrumbs.push({
          name: 'Patrol Details',
          path: location.pathname + location.search, // Keep current path with query params
        });
      } else if (viewParam === 'create-patrol') {
        // If in edit mode, add "Patrol Details" breadcrumb first (since edit comes from patrol details)
        if (isPatrolEditMode) {
          const patrolDetailsParams = new URLSearchParams();
          patrolDetailsParams.set('view', 'patrol-details');
          patrolDetailsParams.set('patrolId', patrolIdParam);
          breadcrumbs.push({
            name: 'Patrol Details',
            path: location.pathname + '?' + patrolDetailsParams.toString(),
          });
        }
        // Add "Create Patrolling Schedule" or "Edit Patrolling Schedule" breadcrumb
        breadcrumbs.push({
          name: isPatrolEditMode ? 'Edit Patrolling Schedule' : 'Create Patrolling Schedule',
          path: location.pathname + location.search, // Keep current path with query params
        });
      } else if (viewParam === 'assign-patrol-convoy') {
        // Add breadcrumbs: if in edit mode, include "Patrol Details" > "Edit Patrolling Schedule" > "Edit Patrol Officers"
        // Otherwise: "Create Patrolling Schedule" > "Patrol Officer Assignment"
        if (isPatrolEditMode) {
          // Add "Patrol Details" breadcrumb first
          const patrolDetailsParams = new URLSearchParams();
          patrolDetailsParams.set('view', 'patrol-details');
          patrolDetailsParams.set('patrolId', patrolIdParam);
          breadcrumbs.push({
            name: 'Patrol Details',
            path: location.pathname + '?' + patrolDetailsParams.toString(),
          });
        }
        // Add "Create Patrolling Schedule"/"Edit Patrolling Schedule" breadcrumb
        const createPatrolParams = new URLSearchParams();
        createPatrolParams.set('view', 'create-patrol');
        if (patrolIdParam) {
          createPatrolParams.set('patrolId', patrolIdParam);
        }
        breadcrumbs.push({
          name: isPatrolEditMode ? 'Edit Patrolling Schedule' : 'Create Patrolling Schedule',
          path: location.pathname + '?' + createPatrolParams.toString(),
        });
        breadcrumbs.push({
          name: isPatrolEditMode ? 'Edit Patrol Officers' : 'Patrol Officer Assignment',
          path: location.pathname + location.search, // Keep current path with query params
        });
      }
    }
    
    // Filter out duplicate consecutive breadcrumbs while preserving order
    // Special handling for Dashboard > Dashboard case
    const uniqueBreadcrumbs = [];
    for (let i = 0; i < breadcrumbs.length; i++) {
      const crumb = breadcrumbs[i];
      const lastCrumb = uniqueBreadcrumbs[uniqueBreadcrumbs.length - 1];
      
      // Allow Dashboard > Dashboard (same path, consecutive)
      if (lastCrumb && crumb.name === 'Dashboard' && lastCrumb.name === 'Dashboard' && crumb.path === lastCrumb.path) {
        uniqueBreadcrumbs.push(crumb);
      } 
      // Skip duplicates that aren't the special Dashboard case
      else if (!lastCrumb || lastCrumb.path !== crumb.path || lastCrumb.name !== crumb.name) {
        uniqueBreadcrumbs.push(crumb);
      }
    }
    
    return uniqueBreadcrumbs;
  };

  const breadcrumbs = getBreadcrumbPath();
  const shouldUseOverlaySidebar = isMobileViewport;

  const filterSidebarModulesByQuery = (modules, query) => {
    const normalizedQuery = (query || '').trim().toLowerCase();
    if (!normalizedQuery) return modules;

    const itemMatches = item =>
      String(item?.name || '')
        .toLowerCase()
        .includes(normalizedQuery);

    const filterItems = items =>
      (items || [])
        .map(item => {
          if (!item) return null;
          if (itemMatches(item)) return item;
          if (!Array.isArray(item.subItems) || item.subItems.length === 0) return null;

          const filteredSubItems = filterItems(item.subItems).filter(Boolean);
          if (filteredSubItems.length === 0) return null;
          return { ...item, subItems: filteredSubItems };
        })
        .filter(Boolean);

    return filterItems(modules);
  };

  const searchedSidebarModules = useMemo(
    () => filterSidebarModulesByQuery(filteredSidebarModules, sidebarSearchQuery),
    [filteredSidebarModules, sidebarSearchQuery]
  );

  const renderSidebarModules = modules =>
    modules.map(module => {
      const isModuleActive =
        openSubMenu === module.name ||
        (module.subItems &&
          module.subItems.some(sub => {
            if (isPageActive(sub.page, activePage)) return true;
            if (sub.subItems) {
              return sub.subItems.some(nested => isPageActive(nested.page, activePage));
            }
            return false;
          })) ||
        activePage === module.page;

      return (
        <div key={module.name} className="w-full">
          <motion.div
            className={combineClasses(
              'px-2.5 py-2 rounded-lg flex items-center gap-2.5 cursor-pointer transition-colors',
              isModuleActive
                ? 'bg-neutral-50/80 outline outline-1 outline-offset-[-1px] outline-green-700/70'
                : 'hover:bg-white/25'
            )}
            onClick={() => {
              if (module.subItems) {
                handleSubMenuToggle(module.name);
              } else {
                navigateToPage(module.page);
              }
            }}
            whileHover={{ x: isSidebarOpen ? 4 : 0 }}
            transition={transition}
          >
            {module.icon &&
              React.cloneElement(module.icon, {
                className: `w-4 h-4 ${
                  isModuleActive ? 'text-green-700' : 'text-neutral-600'
                }`,
              })}
            <div
              className={combineClasses(
                "text-[15px] font-semibold font-['Open_Sans'] leading-6 whitespace-normal break-words",
                isModuleActive ? 'text-green-700' : 'text-neutral-700'
              )}
            >
              {module.name}
            </div>
            {module.hasLLM && (
              <Sparkles className="ml-auto w-3.5 h-3.5 text-yellow-300" />
            )}
            {module.subItems && (
              <div className="ml-auto">
                {openSubMenu === module.name ? (
                  <ChevronUp className="w-3.5 h-3.5 text-neutral-700" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-neutral-700" />
                )}
              </div>
            )}
          </motion.div>

          {isSidebarOpen && module.subItems && openSubMenu === module.name && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="mt-1 ml-2 pl-3 border-l border-dashed border-green-800/90 flex flex-col gap-1"
            >
              {module.subItems.map(subItem => {
                const hasNested =
                  Array.isArray(subItem.subItems) && subItem.subItems.length > 0;
                const isNestedActive =
                  hasNested &&
                  subItem.subItems.some(nested => isPageActive(nested.page, activePage));
                const isSubItemActive =
                  isPageActive(subItem.page, activePage) || isNestedActive;
                const isSectionPanelOpen =
                  openSectionPanel &&
                  openSectionPanel.moduleName === module.name &&
                  openSectionPanel.subItem.name === subItem.name;
                const hasCount = subItem.count != null;

                return (
                  <div key={`${module.name}-${subItem.name}`} className="w-full">
                    <div
                      className={combineClasses(
                        'relative w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md cursor-pointer',
                        isSubItemActive || isSectionPanelOpen
                          ? 'bg-neutral-50/60 text-green-700 font-semibold'
                          : 'text-neutral-600 font-medium hover:bg-white/30'
                      )}
                      onClick={() => {
                        if (hasNested) {
                          openSectionPanelFor(module.name, subItem);
                        } else if (subItem.page) {
                          navigateToPage(subItem.page, module.name);
                        }
                      }}
                    >
                      {isSubItemActive && (
                        <span className="absolute -left-[14px] top-1.5 h-5 w-1 rounded-sm bg-green-700" />
                      )}
                      <div className="text-sm font-['Open_Sans'] leading-snug truncate min-w-0 flex-1">
                        {subItem.name}
                      </div>
                      <div className="flex items-center gap-2">
                        {hasCount && (
                          <span className="min-w-[1.75rem] px-1.5 py-0.5 text-[11px] leading-none text-neutral-700 bg-neutral-200/70 rounded-md text-center">
                            {subItem.count}
                          </span>
                        )}
                        {hasNested && (
                          <ChevronRight className="w-3.5 h-3.5 text-neutral-600" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          )}
        </div>
      );
    });

  const sidebarMenuContent = (
    <div className="w-full px-3 py-4 flex flex-col gap-1">
      <div className="px-1 pb-3">
        <div className="relative">
          <PackageSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <input
            value={sidebarSearchQuery}
            onChange={e => setSidebarSearchQuery(e.target.value)}
            placeholder="Search features..."
            className="w-full pl-9 pr-9 py-2 rounded-xl border border-white/60 bg-white/70 backdrop-blur-md text-sm text-neutral-800 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-green-600/30 focus:border-green-600/40"
          />
          {sidebarSearchQuery.trim() && (
            <button
              type="button"
              onClick={() => setSidebarSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-neutral-200/60 text-neutral-600"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {isLoadingUser ? (
        <div className="flex items-center justify-center p-4">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredSidebarModules.length === 0 ? (
        <div className="text-center p-4 text-gray-500">
          <p>No access available</p>
        </div>
      ) : searchedSidebarModules.length === 0 ? (
        <div className="text-center p-4 text-gray-500">
          <p>No matching features</p>
        </div>
      ) : (
        renderSidebarModules(searchedSidebarModules)
      )}
    </div>
  );

  const sectionPanelContent = openSectionPanel && (
    <div className="h-full flex flex-col w-full overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-3 border-b border-neutral-200/80 shrink-0 bg-white/20">
        <button
          type="button"
          onClick={closeSectionPanel}
          className="p-1.5 rounded-md hover:bg-neutral-100/80 text-neutral-600"
          aria-label="Back"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
        </button>
        <span className="text-sm font-semibold text-green-700 truncate font-['Open_Sans']">
          {openSectionPanel.subItem.name}
        </span>
      </div>
      <div className="flex-1 overflow-y-auto py-2 px-3">
        <div className="ml-1 pl-3 border-l border-dashed border-green-800/90 flex flex-col">
          {openSectionPanel.subItem.subItems.map((nestedItem, idx, arr) => {
          const isActive =
            isPageActive(nestedItem.page, activePage) &&
            (!nestedItem.search ||
              (location.search && location.search.includes(nestedItem.search)));
          return (
            <div
              key={`${openSectionPanel.subItem.name}-${nestedItem.page}-${nestedItem.search || nestedItem.name}`}
              className={combineClasses(
                'cursor-pointer text-sm font-["Open_Sans"] leading-snug rounded-lg px-3 py-2 relative',
                isActive
                  ? 'text-green-700 bg-green-50/80 font-semibold'
                  : 'text-neutral-600 hover:bg-neutral-100/60',
                idx !== arr.length - 1 ? 'border-b border-dashed border-green-800/40' : null
              )}
              onClick={() => {
                if (nestedItem.page) {
                  navigateToPage(
                    nestedItem.page,
                    openSectionPanel.moduleName,
                    nestedItem.search
                  );
                }
              }}
            >
              {isActive && (
                <span className="absolute -left-[14px] top-2 h-5 w-1 rounded-sm bg-green-700" />
              )}
              {nestedItem.name}
            </div>
          );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`min-h-screen font-sans relative ${
        shouldUseOverlaySidebar && isSidebarOpen ? 'overflow-hidden' : 'overflow-y-auto'
      }`}
      style={{
        borderRadius: '12px',
        border: '1px solid #FFF',
        background: 'linear-gradient(135deg, #B3E5FC 0%,rgb(225, 212, 248) 25%, #81D4FA 50%, #C5CAE9 75%, #B39DDB 100%)',
        boxShadow:
          '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
        backdropFilter: 'blur(30px)',
      }}
    >
      {/* New HomePage Header - with enhanced styling */}
      <div className="w-full h-full p-2 flex flex-col gap-2">
        {/* Header */}
        <header className="w-full px-3 py-2 bg-neutral-50/50 rounded-xl shadow-[-2px_-2px_6px_0px_rgba(211,211,211,0.10)] shadow-[2px_2px_6px_0px_rgba(211,211,211,0.10)] outline outline-1 outline-offset-[-1px] outline-white/20 backdrop-blur-[20px] flex justify-between items-center relative z-[10000]">
          <div className="flex justify-start items-center gap-6">
            {/* Menu Toggle Button */}
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg transition-all"
              style={{
                background: 'radial-gradient(184.58% 152.09% at 136.24% -51.3%, rgba(10, 128, 137, 0.09) 0%, rgba(255, 255, 255, 0.72) 100%)',
                boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10), 0px 5px 20.100000381469727px 0px rgba(255,255,255,0.35) inset',
                outline: '1px solid white',
                backdropFilter: 'blur(30px)',
              }}
            >
              <Menu className="w-6 h-6 text-gray-800" />
            </button>
            {/* Logo and App Title */}
            <div className="flex justify-start items-center gap-3">
              <img
                className="w-12 h-12 rounded-xl"
                src={innoworxLogo}
                alt="Innoworx App Logo"
              />
              <div className="text-black text-3xl font-bold font-['Figtree'] leading-10">
                Innoworx.ai
              </div>
            </div>
          </div>
          <div className="flex justify-start items-center gap-4">
            {canInstall && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    const result = await promptInstall();
                    console.log('PWA install choice:', result);
                  } catch (error) {
                    console.error('PWA install prompt failed:', error);
                  }
                }}
                className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 hover:bg-white text-sm font-medium text-gray-900 shadow-sm border border-white/60"
              >
                <span>Install app</span>
              </button>
            )}
            {!isMobileViewport && (
              <div className="inline-flex flex-col justify-start items-end">
                <div className="text-black text-xl font-semibold font-['Open_Sans'] leading-7 whitespace-nowrap">
                  {isLoadingUser ? 'Loading...' : userInfo?.userName || 'User'}
                </div>
                <div className="text-zinc-500 text-base font-normal font-['Open_Sans'] leading-relaxed whitespace-nowrap">
                  {isLoadingUser ? '...' : userInfo?.displayName || 'User'}
                </div>
              </div>
            )}
            <div
              data-property-1="Profile"
              className="w-12 h-12 relative cursor-pointer z-[10000]"
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              ref={profileDropdownRef}
            >
              {isLoadingProfileImage ? (
                <div className="w-12 h-12 left-0 top-0 absolute rounded-xl bg-gray-200 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-gray-400 border-t-blue-600 rounded-full animate-spin"></div>
                </div>
              ) : profileImageUrl ? (
                <img
                  className="w-12 h-12 left-0 top-0 absolute rounded-xl object-cover"
                  src={profileImageUrl}
                  alt="Profile"
                  onError={e => {
                    console.log('🖼️ Image failed to load:', profileImageUrl, e);
                    setProfileImageUrl(null);
                  }}
                  onLoad={() =>
                    console.log(
                      '🖼️ Image loaded successfully:',
                      profileImageUrl
                    )
                  }
                />
              ) : userInfo?.userName ? (
                <div className="w-12 h-12 left-0 top-0 absolute rounded-xl bg-blue-600 flex items-center justify-center text-white text-xl font-bold">
                  {userInfo.userName.charAt(0).toUpperCase()}
                </div>
              ) : (
                <img
                  className="w-12 h-12 left-0 top-0 absolute rounded-xl"
                  src="https://placehold.co/64x64/000000/FFFFFF?text=U"
                  alt="Profile"
                />
              )}
              <AnimatePresence>
                {showProfileDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-2 z-[9999]"
                    style={{ position: 'absolute', top: '100%' }}
                  >
                    <ProfileDropdown
                      onLogout={handleProfileLogout}
                      onProfileClick={handleProfileClick}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        <AnimatePresence>
          {shouldUseOverlaySidebar && isSidebarOpen && (
            <>
              <motion.div
                className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={toggleSidebar}
              />
              <motion.div
                className="fixed top-[72px] left-0 z-50 h-[calc(100%-72px)] w-full max-w-[17rem] sm:max-w-[18rem] bg-neutral-50/95 rounded-r-2xl shadow-[0px_25px_50px_rgba(0,0,0,0.25)] outline outline-1 outline-white/40 backdrop-blur-[30px] overflow-hidden"
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={transition}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {openSectionPanel ? (
                    <motion.div
                      key="overlay-section-panel"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      className="h-full w-full flex flex-col"
                    >
                      <div className="flex items-center gap-2 px-4 pt-4 pb-2 border-b border-white/40 shrink-0">
                        <button
                          type="button"
                          onClick={closeSectionPanel}
                          className="p-2 rounded-full hover:bg-white/60 text-neutral-600"
                          aria-label="Back"
                        >
                          <ChevronRight className="w-5 h-5 rotate-180" />
                        </button>
                        <span className="text-base font-semibold text-green-700 truncate">
                          {openSectionPanel.subItem.name}
                        </span>
                      </div>
                      <div className="flex-1 overflow-y-auto py-2 px-3">
                        <div className="ml-1 pl-3 border-l border-dashed border-green-800/90 flex flex-col">
                          {openSectionPanel.subItem.subItems.map((nestedItem, idx, arr) => {
                            const isActive =
                              isPageActive(nestedItem.page, activePage) &&
                              (!nestedItem.search ||
                                (location.search && location.search.includes(nestedItem.search)));
                            return (
                              <div
                                key={`${openSectionPanel.subItem.name}-${nestedItem.page}-${nestedItem.search || nestedItem.name}`}
                                className={combineClasses(
                                  'cursor-pointer text-sm font-["Open_Sans"] leading-snug rounded-lg px-3 py-2 relative',
                                  isActive
                                    ? 'text-green-700 bg-green-50/80 font-semibold'
                                    : 'text-neutral-600 hover:bg-neutral-100/60',
                                  idx !== arr.length - 1
                                    ? 'border-b border-dashed border-green-800/40'
                                    : null
                                )}
                                onClick={() => {
                                  if (nestedItem.page) {
                                    navigateToPage(
                                      nestedItem.page,
                                      openSectionPanel.moduleName,
                                      nestedItem.search
                                    );
                                  }
                                  if (isMobileViewport) toggleSidebar();
                                }}
                              >
                                {isActive && (
                                  <span className="absolute -left-[14px] top-2 h-5 w-1 rounded-sm bg-green-700" />
                                )}
                                {nestedItem.name}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="overlay-main-menu"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.2 }}
                      className="h-full w-full flex flex-col overflow-hidden"
                    >
                      <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b border-white/40">
                        <span className="text-lg font-semibold text-gray-900">
                          Menu
                        </span>
                        <button
                          className="p-2 rounded-full hover:bg-white/60 transition"
                          onClick={toggleSidebar}
                          aria-label="Close menu"
                        >
                          <X className="w-5 h-5 text-gray-700" />
                        </button>
                      </div>
                      {sidebarMenuContent}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Main Content Area */}
        <div
          className={`flex-1 flex ${
            shouldUseOverlaySidebar ? 'gap-0' : isSidebarOpen ? 'gap-2' : 'gap-0'
          }`}
        >
          {/* Sidebar */}
          {!shouldUseOverlaySidebar && (
            <motion.div
              initial={{ width: isSidebarOpen ? '280px' : '0px' }}
              animate={{ width: isSidebarOpen ? '280px' : '0px' }}
              transition={transition}
              className="flex-shrink-0 relative bg-neutral-50/40 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-white/0 backdrop-blur-[30px] overflow-hidden"
              style={{
                background:
                  'linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), linear-gradient(170deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.40) 97.94%)',
                border: isSidebarOpen ? '1px solid #FFFFFF' : 'none',
              }}
            >
              {isSidebarOpen && (
                <AnimatePresence mode="wait" initial={false}>
                  {openSectionPanel ? (
                    <motion.div
                      key="section-panel"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      className="h-full w-full"
                    >
                      {sectionPanelContent}
                    </motion.div>
                  ) : (
                    <motion.div
                      key="main-menu"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.2 }}
                      className="h-full w-full overflow-y-auto"
                    >
                      {sidebarMenuContent}
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </motion.div>
          )}


          {/* Main Content Area */}
          <div className="flex-1 flex flex-col gap-2 min-w-0 overflow-x-hidden">
            {/* Breadcrumbs */}
            {breadcrumbs.length > 0 && (
            <div className="w-full min-h-[3rem] px-3 sm:px-6 py-3 bg-neutral-50/40 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] backdrop-blur-[30px] flex items-center gap-2.5 overflow-x-auto">
              <div className="flex justify-start items-center gap-3 flex-nowrap">
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={`${location.pathname}-${index}-${crumb.path}`}>
                    <div className="flex justify-center items-center gap-2.5 flex-shrink-0">
                      <div
                        className={combineClasses(
                          "justify-center text-sm sm:text-lg font-normal font-['Open_Sans'] leading-relaxed whitespace-nowrap",
                          index === 0
                            ? 'text-gray-400 cursor-default'
                            : index === breadcrumbs.length - 1
                            ? 'text-black cursor-default'
                            : 'text-gray-400 cursor-pointer hover:text-gray-600'
                        )}
                        onClick={() => {
                          if (index > 0 && index < breadcrumbs.length - 1) {
                            navigate(crumb.path, crumb.state ? { state: crumb.state } : {});
                          }
                        }}
                      >
                        {crumb.name}
                      </div>
                    </div>
                    {index < breadcrumbs.length - 1 && (
                      <ChevronRight className="w-3 h-3 text-black flex-shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            )}

            {/* Content Area */}
            <main
              className="flex-1 bg-neutral-50/40 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] overflow-y-auto overflow-x-hidden min-w-0"
              style={{
                borderRadius: '12px',
                border: '1px solid var(--card-11-stroke, #FFF)',
                background:
                  'linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), linear-gradient(170deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.40) 97.94%)',
                boxShadow:
                  '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
                backdropFilter: 'blur(30px)',
              }}
            >
              <div className="w-full h-full p-2 md:p-4 overflow-x-hidden min-w-0">
                {/* Use Outlet to render child routes from App.js */}
                <Outlet />
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Officer Insight Modal */}
      {showInsightModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={combineClasses(
              'bg-white/15 backdrop-blur-lg rounded-xl border border-white/20 shadow-2xl p-6 w-full max-w-md text-white',
              'flex flex-col items-center justify-center space-y-4'
            )}
          >
            <h2 className="text-2xl font-bold text-green-400 flex items-center">
              Officer Insight <Sparkles className="ml-2 w-6 h-6" />
            </h2>
            {isLoadingInsight ? (
              <div className="flex flex-col items-center">
                <Loader2 className="animate-spin w-8 h-8 text-green-400 mb-2" />
                <p>Generating insight...</p>
              </div>
            ) : errorInsight ? (
              <p className="text-red-400">{errorInsight}</p>
            ) : (
              <p className="text-gray-200 text-center">
                {officerInsight || 'No insight generated yet.'}
              </p>
            )}
            <button
              onClick={() => setShowInsightModal(false)}
              className="mt-4 px-6 py-2 bg-green-500/30 text-green-200 rounded-full hover:bg-green-500/40 transition-colors"
            >
              Close
            </button>
          </motion.div>
        </div>
      )}

      {/* ScheduleShift Overlays are now rendered directly in OverlayContext using React Portal */}
    </div>
  );
};

const HomePage = ({ onLogout, isAuthenticated }) => {
  return (
    <OverlayProvider>
      <HomePageContent onLogout={onLogout} isAuthenticated={isAuthenticated} />
    </OverlayProvider>
  );
};

export default HomePage;
